
import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { View, DrugInfo } from '../types';
import { getAllDrugs } from '../services/pharmacyService';
import { useAuth } from '../contexts/AuthContext';
import { pushClinicianDataToCloud } from '../services/userSyncService';
import SEO from './SEO';

// --- ICONS ---
const SmallLogoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="5" fill="#FACC15"/>
    <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="7" r="2" fill="white"/>
    <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="41" r="2" fill="white"/>
    <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="7" cy="24" r="2" fill="white"/>
    <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="41" cy="24" r="2" fill="white"/>
  </svg>
);
const BackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>;
const StarIcon: React.FC<{ filled: boolean; className?: string }> = ({ filled, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 transition-colors ${className} ${filled ? 'text-yellow-400 fill-current' : 'text-gray-600 hover:text-yellow-500'}`} viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);
const WarningIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M8.257 3.099c.636-1.214 2.852-1.214 3.488 0l6.248 11.921A2 2 0 0116.248 18H3.752a2 2 0 01-1.745-3.02L8.257 3.1zM10 14a1 1 0 100-2 1 1 0 000 2zm-1-4a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
    </svg>
);
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const SearchIcon: React.FC = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;


interface DrugIndexProps {
    onNavigate: (view: View) => void;
    onSelectDrug: (drugId: string) => void;
}

const DrugCard: React.FC<{ drug: DrugInfo; isFavorite: boolean; onToggleFavorite: (id: string) => void; onSelectDrug: (id: string) => void; }> = ({ drug, isFavorite, onToggleFavorite, onSelectDrug }) => (
    <div className="bg-[#15243E] rounded-xl border border-blue-900/50 flex items-center gap-4 transition-all hover:border-yellow-400/50 hover:bg-yellow-400/5 cursor-pointer" onClick={() => onSelectDrug(drug.id)}>
        <div className="flex-grow p-4 overflow-hidden">
            <div className="flex items-start justify-between">
                <h3 className="font-bold text-lg text-white truncate">{drug.brandName}</h3>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    {drug.isUserAdded && <div className="text-xs font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30 px-2 py-0.5 rounded-full">Custom</div>}
                    {drug.hasBlackBoxWarning && <div className="flex items-center gap-1 text-xs font-bold bg-red-500/20 text-red-300 border border-red-500/30 px-2 py-0.5 rounded-full"><WarningIcon /> BBW</div>}
                    {drug.isRx && <div className="text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full">Rx</div>}
                    {drug.isOtc && <div className="text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30 px-2 py-0.5 rounded-full">OTC</div>}
                </div>
            </div>
            <p className="text-gray-400 truncate">{drug.genericName}</p>
            <p className="text-sm text-gray-500 truncate mt-1">{drug.therapeuticClass}</p>
        </div>
        <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(drug.id); }}
            className="p-4 self-stretch flex items-center justify-center border-l border-blue-900/50"
            aria-label={isFavorite ? `Remove ${drug.brandName} from favorites` : `Add ${drug.brandName} to favorites`}
        >
            <StarIcon filled={isFavorite} />
        </button>
    </div>
);


const DrugIndex: React.FC<DrugIndexProps> = ({ onNavigate, onSelectDrug }) => {
    const { uid } = useAuth();
    const [drugs, setDrugs] = useState<DrugInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [favorites, setFavorites] = useState<string[]>([]);
    const [recentlyViewed, setRecentlyViewed] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!uid) return;

        const storedFavorites = localStorage.getItem(`drugFavorites_${uid}`);
        if (storedFavorites) setFavorites(JSON.parse(storedFavorites));
        
        const storedRecent = localStorage.getItem(`drugRecents_${uid}`);
        if (storedRecent) setRecentlyViewed(JSON.parse(storedRecent));

        const fetchDrugs = async () => {
            setIsLoading(true);
            const data = await getAllDrugs(uid);
            setDrugs(data);
            setIsLoading(false);
        };
        fetchDrugs();
    }, [uid]);
    
    const handleToggleFavorite = (drugId: string) => {
        if (!uid) return;
        const newFavorites = favorites.includes(drugId)
            ? favorites.filter(id => id !== drugId)
            : [...favorites, drugId];
        setFavorites(newFavorites);
        localStorage.setItem(`drugFavorites_${uid}`, JSON.stringify(newFavorites));
        pushClinicianDataToCloud(uid, { favorites: newFavorites });
    };

    const handleSelectAndTrack = (drugId: string) => {
        if (!uid) return;
        const newRecents = [drugId, ...recentlyViewed.filter(id => id !== drugId)].slice(0, 4);
        setRecentlyViewed(newRecents);
        localStorage.setItem(`drugRecents_${uid}`, JSON.stringify(newRecents));
        pushClinicianDataToCloud(uid, { recentlyViewed: newRecents });
        onSelectDrug(drugId);
    };
    
    const handleToggleFilter = (filter: string) => {
        setActiveFilters(prev => 
            prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]
        );
    };
    
    const filteredDrugs = useMemo(() => {
        return drugs.filter(drug => {
            const lowerSearch = searchTerm.toLowerCase();
            const matchesSearch = drug.brandName.toLowerCase().includes(lowerSearch) || drug.genericName.toLowerCase().includes(lowerSearch) || drug.therapeuticClass.toLowerCase().includes(lowerSearch);
            
            const matchesFilters = activeFilters.every(filter => {
                if (filter === 'favorites') return favorites.includes(drug.id);
                if (filter === 'bbw') return drug.hasBlackBoxWarning;
                if (filter === 'custom') return drug.isUserAdded;
                return true;
            });

            return matchesSearch && matchesFilters;
        });
    }, [drugs, searchTerm, activeFilters, favorites]);

    const groupedDrugs = useMemo(() => {
        return filteredDrugs.reduce((acc, drug) => {
            const firstLetter = drug.genericName[0].toUpperCase();
            if (!acc[firstLetter]) acc[firstLetter] = [];
            acc[firstLetter].push(drug);
            return acc;
        }, {} as Record<string, DrugInfo[]>);
    }, [filteredDrugs]);

    const favoriteDrugs = useMemo(() => drugs.filter(drug => favorites.includes(drug.id)), [drugs, favorites]);
    const recentlyViewedDrugs = useMemo(() => recentlyViewed.map(id => drugs.find(d => d.id === id)).filter(Boolean) as DrugInfo[], [recentlyViewed, drugs]);

    return (
        <div className="py-6">
            <SEO 
                title="Drug Index - Dr.Rx"
                description="Search and browse detailed monographs for top medications. Access dosing, adverse reactions, and black box warnings."
                url="/?view=drug-index"
                keywords={["Drug Database", "Pharmacy Monographs", "Black Box Warnings", "Dosing Guide", "Generic Drugs", "Brand Name Drugs"]}
            />
            
            <header className="mb-8">
                <div className="flex justify-between items-start mb-4">
                    <button onClick={() => onNavigate('library')} className="flex items-center text-sm text-yellow-400 hover:text-yellow-300 font-semibold">
                        <BackIcon /> Back to Library
                    </button>
                    <button onClick={() => onNavigate('dashboard')} className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-all cursor-pointer hover:scale-105">
                         <SmallLogoIcon />
                         <span className="text-sm font-bold text-white">Dr.<span className="text-yellow-400">Rx</span><sup className="text-xs -top-2">TM</sup></span>
                    </button>
                </div>
                <h1 className="text-4xl font-bold">Drug Index</h1>
                <p className="text-gray-400 mt-2">Browse the Top 100 medications and your custom-added drugs.</p>
            </header>

             <div className="sticky top-0 bg-[#101D35]/90 backdrop-blur-lg z-10 py-4 mb-8 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 border-y border-blue-900/50">
                <div className="relative mb-4 group">
                    <input 
                        ref={inputRef}
                        type="text" 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                        placeholder="Search by brand, generic, or class..." 
                        className="w-full bg-[#1E2E4A] border border-[#3A4A67] rounded-full p-3 pl-6 pr-14 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all shadow-md"
                    />
                    <button 
                        onClick={() => inputRef.current?.focus()}
                        className={`absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all duration-300 ${searchTerm ? 'text-yellow-400 bg-yellow-400/10 scale-110 shadow-[0_0_10px_rgba(250,204,21,0.3)]' : 'text-gray-500 group-hover:text-gray-300'}`}
                        aria-label="Search"
                    >
                        <SearchIcon />
                    </button>
                </div>
                <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
                    <span className="text-sm font-semibold text-gray-400 mr-2 flex-shrink-0">Filters:</span>
                    {['favorites', 'bbw', 'custom'].map(filter => {
                        const isActive = activeFilters.includes(filter);
                        return <button key={filter} onClick={() => handleToggleFilter(filter)} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${isActive ? 'bg-blue-800 text-yellow-300 shadow-md' : 'bg-black/20 text-gray-400 hover:bg-white/10'}`}>
                            {filter === 'bbw' ? 'Has BBW' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                        </button>
                    })}
                </div>
            </div>

            {searchTerm === '' && activeFilters.length === 0 && (
                <>
                    {recentlyViewedDrugs.length > 0 && (
                         <section className="mb-10">
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><ClockIcon /> Recently Viewed</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {recentlyViewedDrugs.map(drug => (
                                   <DrugCard key={drug.id} drug={drug} isFavorite={favorites.includes(drug.id)} onToggleFavorite={handleToggleFavorite} onSelectDrug={handleSelectAndTrack}/>
                                ))}
                            </div>
                        </section>
                    )}
                    {favoriteDrugs.length > 0 && (
                         <section className="mb-10">
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><StarIcon filled className="h-6 w-6" /> Favorites</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {favoriteDrugs.map(drug => (
                                   <DrugCard key={drug.id} drug={drug} isFavorite={favorites.includes(drug.id)} onToggleFavorite={handleToggleFavorite} onSelectDrug={handleSelectAndTrack}/>
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}

            {isLoading && <div className="text-center py-10">Loading drug database...</div>}

            {!isLoading && Object.keys(groupedDrugs).sort().map(letter => (
                <section key={letter} className="mb-10">
                    <h2 className="text-3xl font-bold text-yellow-400 mb-4 pb-2 border-b-2 border-blue-900/50">{letter}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {groupedDrugs[letter].map(drug => (
                            <DrugCard key={drug.id} drug={drug} isFavorite={favorites.includes(drug.id)} onToggleFavorite={handleToggleFavorite} onSelectDrug={handleSelectAndTrack}/>
                        ))}
                    </div>
                </section>
            ))}

            {!isLoading && filteredDrugs.length === 0 && (
                 <div className="text-center py-16 text-gray-400">
                    <p className="text-lg font-semibold">No drugs found</p>
                    <p>Try adjusting your search or filters.</p>
                </div>
            )}
        </div>
    );
};

export default DrugIndex;